import React, { useState, useEffect, useRef } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import {
  LogOut,
  LayoutDashboard,
  FileText,
  CreditCard,
  Wrench,
  FolderOpen,
  Menu,
  X,
  ChevronDown,
  BellRing,
  ChevronLeft,
    ChevronRight,

} from "lucide-react";
import { useAuth } from "../helpers/useAuth";
import { Button } from "./Button";
import { Avatar, AvatarFallback, AvatarImage } from "./Avatar";
import { BrandLogo } from "./BrandLogo";
import { ThemeModeSwitch } from "./ThemeModeSwitch";
import { useUnreadCount, useRefreshNotificationsList } from "../helpers/useNotifications";
import { useScrollToTopOnNavigate } from "../helpers/useScrollToTopOnNavigate";
import { useTenantProfiles } from "../helpers/useTenantProfiles";

import styles from "./TenantPortalLayout.module.css";

const TENANT_COLLAPSE_KEY = "tenantSidebarCollapsed";

interface TenantPortalLayoutProps {
  children: React.ReactNode;
}

export function TenantPortalLayout({ children }: TenantPortalLayoutProps) {
  const { authState, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(TENANT_COLLAPSE_KEY);
    return stored === "true";
  });

  const mainRef = useRef<HTMLElement | null>(null);
  const contentContainerRef = useRef<HTMLDivElement | null>(null);

  useScrollToTopOnNavigate(mainRef, contentContainerRef);

  const toggleCollapse = () => {
    setIsCollapsed((prev) => {
      const newValue = !prev;
      localStorage.setItem(TENANT_COLLAPSE_KEY, String(newValue));
      return newValue;
    });
  };

  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.count ?? 0;
  const refreshNotifications = useRefreshNotificationsList();

  const user = authState.type === "authenticated" ? authState.user : null;
  const tenantProfiles = useTenantProfiles({ enabled: user?.role === "tenant" });
  const activeProfile = tenantProfiles.data?.profiles.find(
    (profile) => profile.tenantId === tenantProfiles.data?.activeTenantId
  );
  

  const navItems = [
    {
      path: "/tenant-portal/dashboard",
      label: "Dashboard",
      icon: <LayoutDashboard size={20} />,
    },
    {
      path: "/tenant-portal/lease",
      label: "Properties",
      icon: <FileText size={20} />,
    },
    {
      path: "/tenant-portal/payments",
      label: "Payments",
      icon: <CreditCard size={20} />,
    },
    {
      path: "/tenant-portal/maintenance",
      label: "Maintenance",
      icon: <Wrench size={20} />,
    },
    {
      path: "/tenant-portal/documents",
      label: "Documents",
      icon: <FolderOpen size={20} />,
    },
  ];

  const isNavItemActive = (path: string) =>
    location.pathname === path ||
    (path !== "/tenant-portal/dashboard" && location.pathname.startsWith(path));

  if (tenantProfiles.data?.selectionRequired && location.pathname !== "/tenant-portal/profiles") {
    return <Navigate to="/tenant-portal/profiles" replace />;
  }

  return (
    <div className={styles.layout}>
      {/* Mobile Header */}
      <header className={styles.mobileHeader}>
        <div className={styles.mobileHeaderLeft}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileMenuOpen((v) => !v)}
            className={styles.menuButton}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
          <div className={styles.brand}>
            <BrandLogo imageClassName={styles.logoImage} />
            <span className={styles.brandName}>
              BhadaPMS<span className={styles.brandDot}>.</span>
            </span>
          </div>
        </div>
        <div className={styles.mobileHeaderRight}>
          <Link to="/tenant-portal/notifications" className={styles.mobileHeaderLink} onClick={refreshNotifications}>
            <BellRing size={24} />
            {unreadCount > 0 && (
              <span className={styles.mobileHeaderBadge}>{unreadCount > 99 ? "99+" : unreadCount}</span>
            )}
          </Link>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`${styles.sidebar} ${isMobileMenuOpen ? styles.sidebarOpen : ""} ${isCollapsed ? styles.sidebarCollapsed : ""}`}
      >
        <div className={styles.sidebarHeader}>
          <div className={styles.brand}>
            <BrandLogo imageClassName={styles.logoImage} />
            {!isCollapsed && (
              <span className={styles.brandName}>
                BhadaPMS<span className={styles.brandDot}>.</span>
              </span>
            )}
          </div>
          <div className={styles.headerActions}>
            {!isCollapsed && <ThemeModeSwitch className={styles.themeSwitch} />}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={toggleCollapse}
              className={styles.collapseButton}
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </Button>
          </div>
        </div>

        {!isCollapsed && (
          <div className={styles.portalBadgeWrapper}>
            <div className={styles.portalBadgeContainer}>
              <span className={styles.portalBadge}>Tenant Portal</span>
            </div>
          </div>
        )}


        <nav className={styles.nav}>
          {navItems.map((item) => {
            const isActive = isNavItemActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`${styles.navItem} ${isActive ? styles.navItemActive : ""}`}
                onClick={() => setIsMobileMenuOpen(false)}
                title={isCollapsed ? item.label : undefined}
              >
                {item.icon}
                {!isCollapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
          <Link
            to="/tenant-portal/notifications"
            className={`${styles.navItem} ${
              location.pathname.startsWith("/tenant-portal/notifications") ? styles.navItemActive : ""
            }`}
            onClick={() => {
              setIsMobileMenuOpen(false);
              refreshNotifications();
            }}
            title={isCollapsed ? "Notifications" : undefined}
          >
            <div className={styles.navIconWrapper}>
              <BellRing size={20} />
              {isCollapsed && unreadCount > 0 && (
                <span className={styles.navBadgeDot}>{unreadCount > 99 ? "99+" : unreadCount}</span>
              )}
            </div>
            {!isCollapsed && (
              <>
                <span className={styles.navItemLabel}>Notifications</span>
                {unreadCount > 0 && (
                  <span className={styles.navBadgePill}>{unreadCount > 99 ? "99+" : unreadCount}</span>
                )}
              </>
            )}
          </Link>
        </nav>

        <div className={styles.mobileThemeRow}>
          <span className={styles.mobileThemeLabel}>Theme</span>
          <ThemeModeSwitch />
        </div>

        

        <div className={styles.userSection}>
          <Link to="/profile/settings" className={styles.userInfo}>
            <Avatar className={styles.avatar}>
              <AvatarImage src={user?.avatarUrl || undefined} />
              <AvatarFallback>
                {user?.displayName?.substring(0, 2).toUpperCase() || "TE"}
              </AvatarFallback>
            </Avatar>
            {!isCollapsed && (
              <div className={styles.userDetails}>
                <div className={styles.userNameRow}>
                  <span className={styles.userName}>
                    {activeProfile?.name ?? activeProfile?.companyName ?? user?.displayName}
                  </span>
                  <ChevronDown size={14} className={styles.userNameChevron} />
                </div>
                <div className={styles.userRoleRow}>
                  <span className={styles.userRole}>Tenant</span>
                </div>
              </div>
            )}
          </Link>
          <Button
            variant="ghost"
            size={isCollapsed ? "icon-sm" : "sm"}
            className={styles.logoutButton}
            onClick={() => logout()}
            title="Log out"
          >
            <LogOut size={16} />
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main ref={mainRef} className={`${styles.main} ${isCollapsed ? styles.mainCollapsed : ""}`}>
        <div ref={contentContainerRef} className={styles.contentContainer}>{children}</div>
      </main>

      {/* Overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div
          className={styles.overlay}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </div>
  );
}

export default TenantPortalLayout;